import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

const API_URL = "https://zpanel.zentragroup.cl/app/api";

const formatCLP = (n) =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

const formatDate = (iso) => {
  if (!iso) return "-";

  const fecha = String(iso).substring(0, 10);
  const [year, month, day] = fecha.split("-");

  if (!year || !month || !day) return "-";

  return `${day}-${month}-${year}`;
};

// Estado de cobro calculado (igual que el backend prepago: la fecha de corte es
// única = facturación, sin días de gracia; se avisa 5 días antes).
const AVISO_PREVIO_DIAS = 5;
const estadoCobro = (expira) => {
  if (!expira) return null;
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const corte = new Date(String(expira).substring(0, 10) + "T00:00:00");
  if (hoy > corte) return ["Suspendido", "#dc2626"];
  const aviso = new Date(corte); aviso.setDate(aviso.getDate() - AVISO_PREVIO_DIAS);
  if (hoy >= aviso) return ["Por vencer", "#d97706"];
  return ["Al día", "#16a34a"];
};

// Mensajes del banner según el ?pago= con que vuelve Webpay.
const PAGO_MSGS = {
  ok:        ["¡Pago exitoso! Tu servicio quedó al día.", true],
  rechazado: ["El pago fue rechazado por el banco. No se realizó ningún cobro.", false],
  cancelado: ["Cancelaste el pago. No se realizó ningún cobro.", false],
  sin_deuda: ["No tienes deuda pendiente por pagar.", true],
  error:     ["Hubo un problema al procesar el pago. Intenta nuevamente.", false],
};

export default function MiPerfil() {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [pagoMsg, setPagoMsg] = useState(null);
  const [seleccion, setSeleccion] = useState(() => new Set());

  // Lee el resultado del pago (?pago=ok|rechazado|...) y limpia la URL.
  useEffect(() => {
    const pago = new URLSearchParams(window.location.search).get("pago");
    if (pago && PAGO_MSGS[pago]) {
      setPagoMsg(PAGO_MSGS[pago]);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Marca/desmarca un servicio del carrito de pago.
  const toggleServicio = (id) => {
    setSeleccion((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Redirige al pago seguro de los servicios seleccionados.
  const pagarSeleccion = (ids) => {
    const token = localStorage.getItem("portal_token");
    if (!token || !ids.length) return;
    window.location.href = `${API_URL}/pagos/iniciar-pago.php?token=${token}&cliente_servicio_ids=${ids.join(",")}`;
  };

  // Paga online un cobro pendiente (p.ej. el proporcional de alineación).
  const pagarPendiente = (pendId) => {
    const token = localStorage.getItem("portal_token");
    if (!token || !pendId) return;
    window.location.href = `${API_URL}/pagos/iniciar-pago.php?token=${token}&pago_pendiente_ids=${pendId}`;
  };

  useEffect(() => {
    const token = localStorage.getItem("portal_token");

    if (!token) {
      navigate("/Cliente");
      return;
    }

    fetch(`${API_URL}/cliente_full.php`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data.success) {
          localStorage.removeItem("portal_token");
          navigate("/Cliente");
          return;
        }

        setClient({
          pagosOnline: !!data.pagos_online,
          nombre: `${data.cliente.nombre || ""} ${data.cliente.apellido || ""}`.trim(),
          rut: data.cliente.rut || "-",
          email: data.cliente.correo || "-",
          telefono: data.cliente.telefono || "-",
          direccion: data.cliente.direccion || "-",

          servicios: (data.servicios || []).map((s) => {
            const precio = Number(s.valor_mensual || 0);
            const pagado = Number(s.monto_pagado || 0);
            const plan = s.tipo_servicio || "-";

            const saldo =
              plan === "UNICO"
                ? Math.max(precio - pagado, 0)
                : precio;

            return {
              id: s.id,
              nombre: s.nombre_servicio || `Servicio #${s.servicio_id}`,
              plan,
              precio,
              pagado,
              saldo,
              expira: s.fecha_expiracion,
              estado: Number(s.estado) === 1 ? "Activo" : "Pagado",
            };
          }),

          comprobantes: (data.pagos || []).map((p) => ({
            id: p.id,
            tipo: p.tipo_pago || "Pago",
            folio: p.folio_boleta || "-",
            fecha: p.fecha_pago,
            monto: p.monto || 0,
            enlace: p.archivo_boleta_url || "#",
          })),

          cobrosPendientes: (data.cobros_pendientes || []).map((c) => ({
            id: c.id,
            monto: Number(c.monto || 0),
            servicio: c.servicio || "Servicio",
            detalle: c.observacion || "",
            hasta: c.periodo_fin || null,
          })),
        });
      })
      .catch(() => {
        navigate("/Cliente");
      });
  }, [navigate]);

  // Al cargar el cliente, deja pre-seleccionados los servicios con saldo.
  useEffect(() => {
    if (!client) return;
    const pagables = client.servicios
      .filter((s) => s.estado === "Activo" && Number(s.saldo) > 0)
      .map((s) => s.id);
    setSeleccion(new Set(pagables));
  }, [client]);

  if (!client) return null;

  const serviciosActivos = client.servicios.filter(
    (s) => s.estado === "Activo"
  );

  const serviciosSeleccionados = serviciosActivos.filter((s) =>
    seleccion.has(s.id)
  );
  const totalSeleccionado = serviciosSeleccionados.reduce(
    (acc, s) => acc + Number(s.saldo || 0),
    0
  );
  const idsPagables = serviciosSeleccionados
    .filter((s) => Number(s.saldo) > 0)
    .map((s) => s.id);

  return (
    <div className="min-h-screen bg-zentra-bg [overflow-x:clip]">
      <Navbar />

      <main className="mx-auto max-w-5xl px-4 pt-16 pb-20">
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
          Mi perfil
        </h1>
        <p className="mt-1 text-sm text-slate-700">
          Revisa tus datos, tus servicios contratados y fechas de expiración.
        </p>

        {pagoMsg && (
          <div
            className={`mt-4 rounded-xl px-4 py-3 text-sm border ${
              pagoMsg[1]
                ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                : "bg-red-50 text-red-700 border-red-200"
            }`}
          >
            {pagoMsg[0]}
          </div>
        )}

        <section className="mt-6 relative overflow-hidden rounded-3xl bg-white/95 text-slate-900 ring-1 ring-slate-200 shadow-[0_24px_70px_rgba(15,23,42,0.4)]">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(800px_260px_at_0%_0%,rgba(32,75,151,0.25),transparent_60%),radial-gradient(800px_260px_at_100%_0%,rgba(54,192,185,0.25),transparent_60%)]" />

          <div className="relative p-6 md:p-8">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                  Datos del titular
                </p>
                <h2 className="mt-2 text-2xl md:text-3xl font-semibold text-slate-900 break-words">
                  {client.nombre}
                </h2>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-full bg-slate-900 text-slate-50 px-3 py-1 text-xs font-mono">
                    RUT&nbsp;{client.rut}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-xs text-slate-600">
                    Cliente Zentra
                  </span>
                </div>

                <dl className="mt-4 space-y-1 text-sm text-slate-700">
                  <div className="flex gap-2">
                    <dt className="w-20 shrink-0 text-slate-500">Correo</dt>
                    <dd className="min-w-0 break-words">{client.email}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-20 shrink-0 text-slate-500">Teléfono</dt>
                    <dd className="min-w-0 break-words">{client.telefono}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-20 shrink-0 text-slate-500">Dirección</dt>
                    <dd className="min-w-0 break-words">{client.direccion}</dd>
                  </div>
                </dl>
              </div>

              <div className="min-w-0 md:border-l md:border-slate-200 md:pl-6">
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                  Resumen de servicios
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  Selecciona los servicios que quieres pagar.
                </p>

                <div className="mt-4 space-y-2 text-sm">
                  {serviciosActivos.length === 0 ? (
                    <div className="rounded-xl bg-slate-50 px-3 py-2 border border-slate-200 text-slate-500">
                      Sin servicios activos.
                    </div>
                  ) : (
                    serviciosActivos.map((s) => {
                      const pagable = client.pagosOnline && Number(s.saldo) > 0;
                      const marcado = seleccion.has(s.id) && pagable;
                      const c = estadoCobro(s.expira);
                      return (
                        <label
                          key={s.id}
                          className={`flex items-center gap-3 rounded-xl px-3 py-2 border ${
                            marcado
                              ? "bg-white border-[var(--brand-1)] ring-1 ring-[var(--brand-1)]"
                              : "bg-slate-50 border-slate-200"
                          } ${pagable ? "cursor-pointer" : "opacity-70"}`}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-[var(--brand-1)]"
                            checked={marcado}
                            disabled={!pagable}
                            onChange={() => toggleServicio(s.id)}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-900 truncate">
                              {s.nombre}{" "}
                              <span className="text-xs text-slate-500">
                                · {s.plan}
                              </span>
                            </p>
                            <p className="text-xs text-slate-500">
                              Vence el {formatDate(s.expira)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-slate-900">
                              {formatCLP(s.saldo)}
                            </p>
                            {c ? (
                              <span
                                className="mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold text-white"
                                style={{ backgroundColor: c[1] }}
                              >
                                {c[0]}
                              </span>
                            ) : (
                              <span className="mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
                                {s.estado || "Sin estado"}
                              </span>
                            )}
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.14em] text-slate-500">
                      Total a pagar
                    </p>
                    <span className="text-2xl font-semibold text-slate-900">
                      {formatCLP(totalSeleccionado)}
                    </span>
                  </div>
                  {client.pagosOnline && (
                    <button
                      onClick={() => pagarSeleccion(idsPagables)}
                      disabled={idsPagables.length === 0}
                      className="rounded-full px-5 py-2 text-sm shadow-sm transition btn-brand hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Pagar{idsPagables.length > 1 ? ` (${idsPagables.length})` : ""}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {client.cobrosPendientes.length > 0 && (
              <div className="mt-7 border-t border-slate-200 pt-5">
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500 mb-1">
                  Cobros pendientes
                </p>
                <p className="text-sm text-slate-600 mb-3">
                  Cobros opcionales que puedes pagar en línea. Si no los pagas, tus
                  servicios siguen su facturación normal.
                </p>
                <div className="space-y-2">
                  {client.cobrosPendientes.map((c) => (
                    <div
                      key={c.id}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900">
                          {c.servicio}
                          {c.hasta ? " — alinear facturación" : ""}
                        </p>
                        {c.detalle && (
                          <p className="text-xs text-slate-600">{c.detalle}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-slate-900">
                          {formatCLP(c.monto)}
                        </span>
                        {client.pagosOnline && (
                          <button
                            onClick={() => pagarPendiente(c.id)}
                            className="rounded-full px-4 py-2 text-sm shadow-sm transition btn-brand hover:brightness-110"
                          >
                            {c.hasta ? "Pagar y alinear" : "Pagar"}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-7 border-t border-slate-200 pt-5">
              <div className="flex items-center justify-between gap-2 mb-3">
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                  Boletas y facturas
                </p>
                <span className="text-xs text-slate-500">
                  {client.comprobantes.length}{" "}
                  {client.comprobantes.length === 1
                    ? "comprobante"
                    : "comprobantes"}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th className="py-2 pr-4">Tipo</th>
                      <th className="py-2 pr-4">Folio</th>
                      <th className="py-2 pr-4">Fecha</th>
                      <th className="py-2 pr-4">Monto</th>
                      <th className="py-2">Documento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {client.comprobantes.map((c) => (
                      <tr
                        key={c.id}
                        className="border-t border-slate-100 last:border-b"
                      >
                        <td className="py-2 pr-4 text-slate-900">{c.tipo}</td>
                        <td className="py-2 pr-4 text-slate-700">{c.folio}</td>
                        <td className="py-2 pr-4 text-slate-700">
                          {formatDate(c.fecha)}
                        </td>
                        <td className="py-2 pr-4 text-slate-900">
                          {formatCLP(c.monto)}
                        </td>
                        <td className="py-2">
                          {c.enlace && c.enlace !== "#" ? (
                            <a
                              href={c.enlace}
                              download
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-700 hover:border-[var(--brand-1)] hover:text-[var(--brand-1)]"
                            >
                              Descargar PDF
                            </a>
                          ) : (
                            <span className="text-xs text-slate-400">Sin archivo</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="mt-2 text-xs text-slate-500">
                Si necesitas una copia de una boleta o factura específica,
                también puedes solicitarla a soporte@zentragroup.cl.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}